import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { fileUrl } from "@/lib/images";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PriceInput } from "@/components/PriceInput";
import { curSym } from "@/lib/company";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronDown, Loader2, PackagePlus, Upload, X, Star, Link2, ImageIcon, ScanLine, Check, Radio, Store, Warehouse, Sparkles } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { channelLabels } from "@/lib/sectorVisibility";

const UNITS = ["adet", "kg", "koli", "çuval", "teneke", "litre", "paket", "gram"];
const isWeighedUnit = (u) => ["kg", "kilo", "kilogram", "gram", "gr"].includes(String(u || "").trim().toLowerCase());
// Kaydedilen birim backend'de Title-case olabilir ('Kg'); form seçenekleri küçük harf → eşle
const normUnit = (u) => String(u || "").trim().toLowerCase() || "adet";
const MARKET_CODES = ["PER", "ETC", "PAZAR"];
const WHOLESALE_CODES = ["TOP", "ROTA"];

const empty = {
  name: "", subcategory: "", brand: "", no_brand: false,
  market_categories: [], wholesale_categories: [],
  base_unit: "adet", scale_plu: "", vat_rate: 20, barcode: "", tracks_expiry: false, expiry_date: "", low_stock_threshold: "",
  orderable: true,
  images: [], channels: {},
};

export default function ProductForm({ open, onClose, onSaved, editProduct }) {
  const [form, setForm] = useState(empty);
  const [mCats, setMCats] = useState([]);
  const [wCats, setWCats] = useState([]);
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [bcLoading, setBcLoading] = useState(false);
  const { user, activeSector, activeSectorObj } = useAuth();
  const chLabels = channelLabels(activeSectorObj);
  const isAdmin = user?.role === "admin";  // Yönetici Sınırsız: zorunlu alan katılığını atlar

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const [m, t, ch, br] = await Promise.all([
        api.get(`/categories?channel=market`).catch(() => ({ data: [] })),
        api.get(`/categories?channel=toptan`).catch(() => ({ data: [] })),
        api.get("/sales-channels").catch(() => ({ data: [] })),
        api.get("/products/brands").catch(() => ({ data: { brands: [] } })),
      ]);
      if (!alive) return;
      const mList = m.data || [], wList = t.data || [];
      setMCats(mList); setWCats(wList); setChannels(ch.data || []);
      setBrands(br.data?.brands || []);
      const mNames = new Set(mList.map((c) => c.name));
      const wNames = new Set(wList.map((c) => c.name));
      if (editProduct) {
        const chMap = {};
        const cc = editProduct.channel_content || {};
        (editProduct.channel_prices || []).forEach((cp) => {
          const c = cc[cp.code] || {};
          chMap[cp.code] = { active: !!cp.active, price: cp.price ? String(cp.price) : "", min_order: cp.min_order || 1, allocated_stock: cp.allocated_stock != null ? String(cp.allocated_stock) : "", image: c.image || "", description: c.description || "" };
        });
        let mc = editProduct.market_categories || [];
        let wc = editProduct.wholesale_categories || [];
        if (!mc.length && !wc.length) {
          // Legacy ürün: birleşik kategorileri kanal üyeliğine göre ayır
          const all = editProduct.categories?.length ? editProduct.categories : (editProduct.category ? [editProduct.category] : []);
          mc = all.filter((c) => mNames.has(c) || (!mNames.has(c) && !wNames.has(c)));
          wc = all.filter((c) => wNames.has(c));
        }
        setForm({
          ...empty, ...editProduct,
          base_unit: normUnit(editProduct.base_unit),
          market_categories: mc, wholesale_categories: wc,
          expiry_date: editProduct.expiry_date ? String(editProduct.expiry_date).slice(0, 10) : "",
          low_stock_threshold: editProduct.low_stock_threshold ?? "",
          images: editProduct.images || [],
          channels: chMap,
        });
      } else setForm(empty);
    })();
    return () => { alive = false; };
  }, [open, editProduct]);

  const primaryCat = form.market_categories[0] || form.wholesale_categories[0] || "";
  const subs = [...mCats, ...wCats].find((c) => c.name === primaryCat)?.subcategories || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const brandSelectValue = form.no_brand ? "__none__" : (form.brand || "");
  const onBrandSelect = (v) => {
    if (v === "__none__") setForm((f) => ({ ...f, no_brand: true, brand: "" }));
    else setForm((f) => ({ ...f, no_brand: false, brand: v }));
  };
  const setCh = (code, patch) => setForm((f) => ({ ...f, channels: { ...f.channels, [code]: { ...(f.channels[code] || { active: false, price: "", min_order: 1 }), ...patch } } }));
  const toggleMCat = (name) => setForm((f) => {
    const has = f.market_categories.includes(name);
    return { ...f, market_categories: has ? f.market_categories.filter((c) => c !== name) : [...f.market_categories, name] };
  });
  const toggleWCat = (name) => setForm((f) => {
    const has = f.wholesale_categories.includes(name);
    return { ...f, wholesale_categories: has ? f.wholesale_categories.filter((c) => c !== name) : [...f.wholesale_categories, name] };
  });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Yükleme başarısız");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    setForm((f) => ({ ...f, images: [...f.images, urlInput.trim()] }));
    setUrlInput("");
  };
  const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const makePrimary = (i) => setForm((f) => {
    const arr = [...f.images]; const [x] = arr.splice(i, 1); arr.unshift(x); return { ...f, images: arr };
  });

  const lookupBarcode = async (code) => {
    const bc = String(code || form.barcode || "").trim();
    if (!bc) return;
    setBcLoading(true);
    try {
      const { data } = await api.post("/products/barcode-lookup", { barcode: bc, hint: form.name || "" });
      if (data.found) {
        toast.warning(`Bu barkod zaten kayıtlı: ${data.product.name}`);
      } else if (data.suggestion) {
        setForm((f) => ({ ...f, name: data.suggestion.name || f.name, brand: data.suggestion.brand || f.brand, no_brand: false }));
        if (data.suggestion.brand && !brands.includes(data.suggestion.brand)) setBrands((b) => [...b, data.suggestion.brand]);
        toast.success("Barkod önerisi dolduruldu — kontrol edip kaydedin");
      } else {
        toast.info(data.message || "Öneri bulunamadı");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Barkod sorgusu başarısız");
    } finally { setBcLoading(false); }
  };

  const requestSubmit = () => {
    if (!form.name.trim()) return toast.error("Ürün adı zorunlu");
    if (!editProduct && !form.no_brand && !(form.brand || "").trim())
      return toast.error("Marka girin ya da 'Markasız' seçin");
    if (!isAdmin) {
      if (form.market_categories.length + form.wholesale_categories.length === 0)
        return toast.error("En az bir kategori seçin (Market veya Toptan)");
      const active = channels.filter((c) => form.channels[c.code]?.active);
      if (active.length === 0) return toast.error("En az bir satış kanalı seçin (aktif edin + fiyat girin)");
      for (const c of active) {
        if (!(Number(form.channels[c.code]?.price) > 0)) return toast.error(`${c.name} için geçerli fiyat girin (0 olamaz)`);
      }
      const marketActive = active.some((c) => MARKET_CODES.includes(c.code));
      const wholesaleActive = active.some((c) => WHOLESALE_CODES.includes(c.code));
      if (marketActive && form.market_categories.length === 0)
        return toast.error("Market kanalı açık — en az bir Market kategorisi seçin");
      if (wholesaleActive && form.wholesale_categories.length === 0)
        return toast.error("Toptan kanalı açık — en az bir Toptan kategorisi seçin");
    }
    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    setSaving(true);
    try {
      const channel_prices = channels.map((c) => ({
        code: c.code,
        price: Number(form.channels[c.code]?.price) || 0,
        active: !!form.channels[c.code]?.active,
        min_order: Number(form.channels[c.code]?.min_order) || 1,
        allocated_stock: (form.channels[c.code]?.allocated_stock ?? "") !== "" ? Number(form.channels[c.code]?.allocated_stock) : null,
      }));
      const perActive = form.channels["PER"]?.active;
      const wholesale = form.channels["TOP"]?.active || form.channels["ROTA"]?.active;
      const channel_type = perActive ? "market" : (wholesale ? "toptan" : "market");
      const channel_content = {};
      channels.forEach((c) => {
        const s = form.channels[c.code];
        if (s && s.active && (s.image || s.description)) channel_content[c.code] = { image: s.image || "", description: s.description || "" };
      });
      const allCats = [...new Set([...form.market_categories, ...form.wholesale_categories])];
      const payload = {
        name: form.name.trim(), channel_type,
        category: primaryCat, categories: allCats,
        market_categories: form.market_categories, wholesale_categories: form.wholesale_categories,
        subcategory: form.subcategory || null, brand: form.no_brand ? null : (form.brand || null), no_brand: !!form.no_brand,
        base_unit: form.base_unit, scale_plu: isWeighedUnit(form.base_unit) ? (form.scale_plu || null) : null,
        vat_rate: Number(form.vat_rate) || 0,
        barcode: form.barcode || null, tracks_expiry: !!form.tracks_expiry,
        expiry_date: form.tracks_expiry ? (form.expiry_date || null) : null,
        low_stock_threshold: form.low_stock_threshold !== "" && form.low_stock_threshold != null ? Number(form.low_stock_threshold) : null,
        orderable: form.orderable !== false,
        images: form.images,
        channel_prices,
        channel_content,
        // Yeni ürün aktif sektöre etiketlenir (sızıntı önlenir); düzenlemede gönderilmez → backend mevcut etiketi korur.
        ...(editProduct ? {} : (activeSector && activeSector !== "full" ? { sectors: [activeSector] } : {})),
      };
      const res = editProduct ? await api.put(`/products/${editProduct.id}`, payload) : await api.post("/products", payload);
      if (res.data?.pending_approval) toast.info(res.data.message || "Değişiklik Yönetici onayına gönderildi");
      else toast.success(editProduct ? "Ürün güncellendi" : "Ürün kartı oluşturuldu");
      setConfirmOpen(false);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const Sel = ({ value, onChange, children, testid }) => (
    <div className="relative mt-1">
      <select data-testid={testid} value={value} onChange={onChange}
        className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );

  const CatChips = ({ list, selected, onToggle, prefix, activeClass }) => (
    <div className="flex flex-wrap gap-1.5">
      {list.map((c) => {
        const sel = selected.includes(c.name);
        const primary = selected[0] === c.name;
        return (
          <button type="button" key={c.id} data-testid={`${prefix}-${c.name}`} onClick={() => onToggle(c.name)}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${sel ? activeClass : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
            {sel && <Check className="h-3 w-3" />}{c.name}{primary && <span className="ml-1 rounded bg-white/25 px-1 text-[8px]">ANA</span>}
          </button>
        );
      })}
      {list.length === 0 && <span className="text-xs text-slate-400">Kategori yok</span>}
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <PackagePlus className="h-5 w-5 text-brand-green" /> {editProduct ? "Ürünü Düzenle" : "Yeni Ürün Kartı"}
          </DialogTitle>
          <DialogDescription>Tek karttan yönetin: bilgiler, {chLabels.market}/{chLabels.toptan} kategorileri (ayrı), görseller ve satış kanallarına göre fiyatlar.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          {/* Images */}
          <div className="rounded-md bg-slate-50 p-3">
            <p className="overline mb-2 flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Ürün Görselleri</p>
            <div className="flex flex-wrap gap-2">
              {form.images.map((img, i) => (
                <div key={i} className="group relative h-16 w-16 overflow-hidden rounded-md ring-1 ring-slate-200">
                  <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                  {i === 0 && <span className="absolute left-0 top-0 bg-brand-green px-1 text-[8px] font-bold text-white">ANA</span>}
                  <div className="absolute inset-0 hidden items-center justify-center gap-1 bg-black/50 group-hover:flex">
                    {i !== 0 && <button type="button" title="Ana yap" onClick={() => makePrimary(i)} className="rounded bg-white/90 p-1"><Star className="h-3 w-3" /></button>}
                    <button type="button" data-testid={`pf-img-remove-${i}`} title="Sil" onClick={() => removeImg(i)} className="rounded bg-white/90 p-1"><X className="h-3 w-3 text-red-600" /></button>
                  </div>
                </div>
              ))}
              <button type="button" data-testid="pf-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex h-16 w-16 flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-300 text-slate-400 hover:border-brand-green hover:text-brand-green">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[9px]">Yükle</span></>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
            </div>
            <div className="mt-2 flex gap-2">
              <div className="relative flex-1">
                <Link2 className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input data-testid="pf-img-url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="veya görsel bağlantısı (URL) yapıştır" className="h-9 pl-8 text-xs" />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addUrl} className="h-9">Ekle</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Ürün Adı *</Label>
              <Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" placeholder="Örn. Domates" /></div>

            {/* Kanal-bazlı BAĞIMSIZ kategoriler (Market vs Toptan ASLA karışmaz) */}
            <div className="col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5">
                <Label className="flex items-center gap-1.5 text-emerald-800">
                  <Store className="h-3.5 w-3.5" /> {chLabels.market} Kategorileri
                </Label>
                <p className="mb-1.5 text-[10px] text-slate-400">Perakende / market satış kanalları için (ilki birincil)</p>
                <div data-testid="pf-market-categories">
                  <CatChips list={mCats} selected={form.market_categories} onToggle={toggleMCat}
                    prefix="pf-mcat" activeClass="bg-brand-green text-white ring-brand-green" />
                </div>
              </div>
              <div className="rounded-md border border-orange-200 bg-orange-50/40 p-2.5">
                <Label className="flex items-center gap-1.5 text-orange-800">
                  <Warehouse className="h-3.5 w-3.5" /> {chLabels.toptan} Kategorileri
                </Label>
                <p className="mb-1.5 text-[10px] text-slate-400">Toptan (gel-al / rota) satış kanalları için</p>
                <div data-testid="pf-wholesale-categories">
                  <CatChips list={wCats} selected={form.wholesale_categories} onToggle={toggleWCat}
                    prefix="pf-wcat" activeClass="bg-brand-orange text-white ring-brand-orange" />
                </div>
              </div>
            </div>

            <div><Label>Marka *</Label>
              <Sel testid="pf-brand-select" value={brandSelectValue} onChange={(e) => onBrandSelect(e.target.value)}>
                <option value="">Marka seçin…</option>
                <option value="__none__">Markasız</option>
                {form.brand && !brands.includes(form.brand) && <option value={form.brand}>{form.brand}</option>}
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </Sel>
              {form.no_brand && <p className="mt-1 text-[11px] text-slate-400">Bu ürün <b>markasız</b> olarak kaydedilecek.</p>}</div>
            <div><Label>Alt Kategori</Label>
              <Sel testid="pf-subcategory" value={form.subcategory || ""} onChange={(e) => set("subcategory", e.target.value)}>
                <option value="">Seçin...</option>
                {subs.map((s) => <option key={s} value={s}>{s}</option>)}
              </Sel></div>
            <div><Label>Birim</Label>
              <Sel testid="pf-unit" value={form.base_unit} onChange={(e) => set("base_unit", e.target.value)}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </Sel></div>
            {isWeighedUnit(form.base_unit) && (
              <div className="col-span-2"><Label>Tartı PLU Kodu (elektronik terazi)</Label>
                <Input data-testid="pf-scale-plu" value={form.scale_plu || ""} onChange={(e) => set("scale_plu", e.target.value.replace(/\D/g, "").slice(0, 5))}
                  className="mt-1 font-mono" placeholder="ör. 42 — terazi etiketindeki ürün kodu" inputMode="numeric" />
                <p className="mt-1 text-[11px] text-slate-400">Kiloyla satılan ürün. Terazi barkodu (2 ile başlayan) bu PLU + ağırlığı içerir; kasada otomatik kilo yüklenir.</p>
              </div>
            )}
            <div><Label>KDV %</Label>
              <Input data-testid="pf-vat" type="number" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="mt-1 tabular" /></div>
            <div className="col-span-2"><Label>Barkod</Label>
              <div className="mt-1 flex gap-1.5">
                <Input data-testid="pf-barcode" value={form.barcode || ""} onChange={(e) => set("barcode", e.target.value)} className="font-mono" placeholder="Okutun veya yazın" />
                <Button type="button" data-testid="pf-barcode-scan" variant="outline" size="icon" onClick={() => setScanOpen(true)} className="h-10 w-10 shrink-0" title="Barkod okut">
                  <ScanLine className="h-4 w-4 text-brand-green" />
                </Button>
                <Button type="button" data-testid="pf-barcode-suggest" variant="outline" onClick={() => lookupBarcode()} disabled={bcLoading || !(form.barcode || "").trim()} className="h-10 shrink-0" title="Barkoddan ürün adı öner">
                  {bcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1 h-4 w-4 text-brand-orange" /> Ad Öner</>}
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Barkodu okutunca ürün adını marka + miktar + birim olarak otomatik önerir; kontrol edip kaydedin.</p>
            </div>
          </div>

          <div className="rounded-md bg-slate-50 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="cursor-pointer">SKT Takibi</Label>
                <p className="text-[11px] text-slate-400">Son kullanma tarihi ve lot takibi</p>
              </div>
              <Switch data-testid="pf-tracks-expiry" checked={form.tracks_expiry} onCheckedChange={(v) => set("tracks_expiry", v)} />
            </div>
            {form.tracks_expiry && (
              <div className="mt-3 border-t border-slate-200 pt-3">
                <Label className="text-[11px] text-slate-500">Son Kullanma Tarihi (SKT)</Label>
                <DatePickerTR testid="pf-expiry-date" value={form.expiry_date}
                  onChange={(v) => set("expiry_date", v)} className="h-9 w-full sm:w-56" />
              </div>
            )}
          </div>

          <div className="rounded-md bg-emerald-50/60 px-3 py-2.5 ring-1 ring-inset ring-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <Label className="cursor-pointer">Siparişe Açık (Talep Belgesi)</Label>
                <p className="text-[11px] text-slate-400">Açıkken mağazalar bu ürünü depodan <b>Talep Belgesi</b> ile isteyebilir. Kapalıysa mağaza talep listesinde görünmez.</p>
              </div>
              <Switch data-testid="pf-orderable" checked={form.orderable !== false} onCheckedChange={(v) => set("orderable", v)} />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Düşük Stok Eşiği (adet)</Label>
            <Input data-testid="pf-low-stock" type="number" min="0" value={form.low_stock_threshold}
              onChange={(e) => set("low_stock_threshold", e.target.value)} className="mt-1 h-9 w-full tabular sm:w-56" placeholder="Kategori / genel varsayılan" />
          </div>

          {/* Kanal bazlı fiyatlandırma */}
          <div>
            <p className="overline mb-1 flex items-center gap-1"><Radio className="h-3.5 w-3.5" /> Satış Kanalları & Fiyatlar</p>
            <p className="mb-2 text-[11px] text-slate-400">Ürünün satılacağı kanalları aç, her kanal için {curSym()} fiyatını (KDV dahil) gir. {chLabels.market} kanalı açarsan {chLabels.market}, {chLabels.toptan} kanalı açarsan {chLabels.toptan} kategorisi zorunlu.</p>
            <div className="space-y-2">
              {channels.map((c) => {
                const st = form.channels[c.code] || { active: false, price: "", min_order: 1 };
                const isW = WHOLESALE_CODES.includes(c.code);
                return (
                  <div key={c.code} data-testid={`pf-channel-${c.code}`} className={`rounded-md border p-2.5 transition-colors ${st.active ? (isW ? "border-brand-orange bg-orange-50/40" : "border-brand-green bg-emerald-50/40") : "border-slate-200 bg-white"}`}>
                    <div className="flex items-center gap-3">
                      <Switch data-testid={`pf-channel-active-${c.code}`} checked={!!st.active} onCheckedChange={(v) => setCh(c.code, { active: v })} />
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{c.name} {isW && <span className="ml-1 rounded bg-orange-100 px-1 text-[9px] font-bold text-orange-700">{chLabels.toptan.toUpperCase()}</span>}</p>
                        <p className="font-mono text-[10px] text-slate-400">{c.code}</p>
                      </div>
                      {st.active && (
                        <div className="flex items-end gap-2">
                          <div>
                            <Label className="text-[10px] text-slate-500">Fiyat {curSym()} *</Label>
                            <PriceInput data-testid={`pf-channel-price-${c.code}`} type="number" min="0" value={st.price}
                              onChange={(e) => setCh(c.code, { price: e.target.value })} wrapperClassName="mt-0.5 w-28" className="h-9 w-full tabular" placeholder="0,00" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Min. Adet</Label>
                            <Input data-testid={`pf-channel-min-${c.code}`} type="number" min="1" value={st.min_order}
                              onChange={(e) => setCh(c.code, { min_order: e.target.value })} className="mt-0.5 h-9 w-20 tabular" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Kanal Stok</Label>
                            <Input data-testid={`pf-channel-alloc-${c.code}`} type="number" min="0" value={st.allocated_stock || ""}
                              onChange={(e) => setCh(c.code, { allocated_stock: e.target.value })} className="mt-0.5 h-9 w-20 tabular" placeholder="Tümü" title="Bu kanala ayrılacak stok tavanı (boş = tüm stok). Aşırı satışı önler." />
                          </div>
                        </div>
                      )}
                    </div>
                    {st.active && (
                      <div className="mt-2 grid grid-cols-1 gap-2 border-t border-emerald-200/60 pt-2 sm:grid-cols-2">
                        <div>
                          <Label className="text-[10px] text-slate-500">Kanala özel görsel (URL)</Label>
                          <Input data-testid={`pf-channel-img-${c.code}`} value={st.image || ""}
                            onChange={(e) => setCh(c.code, { image: e.target.value })} className="mt-0.5 h-8 text-xs" placeholder="Bu kanala özel görsel bağlantısı (opsiyonel)" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Kanala özel açıklama</Label>
                          <textarea data-testid={`pf-channel-desc-${c.code}`} value={st.description || ""}
                            onChange={(e) => setCh(c.code, { description: e.target.value })} rows={1}
                            className="mt-0.5 w-full rounded-md border border-input bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green"
                            placeholder="Pazaryeri/vitrin açıklaması (opsiyonel)" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {channels.length === 0 && <p className="text-xs text-slate-400">Satış kanalı bulunamadı. Ürünler sayfasındaki "Kanallar" ile ekleyin.</p>}
            </div>
          </div>

          <Button data-testid="pf-submit" onClick={requestSubmit} disabled={saving} className="w-full bg-brand-orange text-white hover:bg-orange-600">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editProduct ? "Kaydet" : "Ürünü Oluştur"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)}
      onDetected={(code) => { set("barcode", code); setScanOpen(false); toast.success("Barkod okundu: " + code); lookupBarcode(code); }} />
    <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} loading={saving} onConfirm={doSubmit}
      title={editProduct ? "Ürünü güncelle" : "Ürün kartını oluştur"}
      description={`"${form.name}" için girdiğiniz bilgileri ve kanal fiyatlarını kaydetmek istediğinize emin misiniz?`}
      confirmText={editProduct ? "Evet, kaydet" : "Evet, oluştur"} />
    </>
  );
}
